import { z } from 'zod';
import { ROLES } from './roles';

// ─────────────── Wallet & payments (Phase 6) ───────────────

export const PAYMENT_GATEWAYS = ['manual', 'chapa'] as const;
export type PaymentGateway = (typeof PAYMENT_GATEWAYS)[number];

export const createPaymentSchema = z.object({
  purpose: z.enum(['CREDIT_PACKAGE', 'AGENT_SUBSCRIPTION']).default('CREDIT_PACKAGE'),
  packageId: z.string().uuid().optional(),
  couponCode: z.string().trim().max(32).optional(),
  gateway: z.enum(PAYMENT_GATEWAYS).default('manual'),
  /** Screenshot of the transfer (manual payments). */
  evidenceKey: z.string().max(200).optional(),
  idempotencyKey: z.string().min(8).max(64),
});
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;

export const couponCheckSchema = z.object({
  code: z.string().trim().min(1).max(32),
  packageId: z.string().uuid(),
});

export const denyPaymentSchema = z.object({ reason: z.string().trim().min(2).max(300) });

export const creditPackageSchema = z.object({
  nameEn: z.string().trim().min(1).max(60),
  nameAm: z.string().trim().min(1).max(60),
  credits: z.number().int().min(1).max(1000),
  /** Price in birr. */
  price: z.number().min(0).max(1_000_000),
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});
export type CreditPackageInput = z.infer<typeof creditPackageSchema>;

export const promotionSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3)
      .max(32)
      .transform((s) => s.toUpperCase())
      .refine((s) => /^[A-Z0-9_-]+$/.test(s), 'Letters, numbers, - and _ only'),
    discountType: z.enum(['PERCENT', 'FIXED']),
    /** Percent (1–100) or birr. */
    value: z.number().positive(),
    maxDiscount: z.number().positive().nullable().optional(),
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    usageLimit: z.number().int().positive().nullable().optional(),
    perUserLimit: z.number().int().min(1).default(1),
    bannerEn: z.string().trim().max(120).nullable().optional(),
    bannerAm: z.string().trim().max(120).nullable().optional(),
    status: z.enum(['ACTIVE', 'PAUSED']).default('ACTIVE'),
  })
  .refine((p) => p.endAt > p.startAt, { message: 'End must be after start', path: ['endAt'] })
  .refine((p) => p.discountType !== 'PERCENT' || p.value <= 100, { message: 'At most 100%', path: ['value'] });
export type PromotionInput = z.infer<typeof promotionSchema>;

/** Discount in minor units for a price, never more than the price itself. */
export function couponDiscountMinor(
  promo: { discountType: string; value: number; maxDiscountMinor: number | null },
  priceMinor: number,
): number {
  let d = promo.discountType === 'PERCENT' ? Math.floor((priceMinor * promo.value) / 100) : promo.value * 100;
  if (promo.maxDiscountMinor !== null && promo.maxDiscountMinor >= 0) d = Math.min(d, promo.maxDiscountMinor);
  return Math.max(0, Math.min(d, priceMinor));
}

export const adjustCreditsSchema = z.object({
  credits: z.number().int().min(-1000).max(1000).refine((n) => n !== 0, 'Not zero'),
  note: z.string().trim().min(2).max(200),
});

// ─────────────── Agents (Phase 7) ───────────────

export const agentActiveSchema = z.object({ active: z.boolean() });

export const editSuggestionSchema = z
  .object({
    displayName: z.string().trim().min(2).max(40).optional(),
    bio: z.string().trim().max(70).optional(),
    /** Birr per session length in minutes, e.g. { "60": 800 }. */
    prices: z.record(z.string().regex(/^\d+$/), z.number().int().min(1).max(1_000_000)).optional(),
  })
  .refine((c) => c.displayName !== undefined || c.bio !== undefined || (c.prices && Object.keys(c.prices).length > 0), {
    message: 'NOTHING_TO_CHANGE',
  });
export type EditSuggestion = z.infer<typeof editSuggestionSchema>;

export const AGENT_PERIODS = ['today', 'week', 'month', 'last_month'] as const;
export type AgentPeriod = (typeof AGENT_PERIODS)[number];

function zonedParts(date: Date, timeZone: string) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((a, x) => ((a[x.type] = x.value), a), {});
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second };
}

/** How far a time zone is ahead of UTC at a moment, in ms. */
function zoneOffset(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(date.getTime() / 1000) * 1000;
}

/** The moment local midnight starts on a local calendar date (month/day may overflow, like Date.UTC). */
export function zonedMidnight(y: number, m: number, d: number, timeZone: string): Date {
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - zoneOffset(new Date(guess), timeZone);
  return new Date(guess - zoneOffset(new Date(first), timeZone));
}

/** Local calendar date "YYYY-MM-DD" and time "HH:MM" in a time zone. */
export function zonedDate(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}
export function zonedTime(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;
}

/** Start and end of a reporting period in a time zone (weeks start on Monday). */
export function periodRange(period: AgentPeriod, now: Date, timeZone: string): { from: Date; to: Date } {
  const { y, m, d } = zonedParts(now, timeZone);
  const end = new Date(now.getTime() + 1);
  switch (period) {
    case 'today':
      return { from: zonedMidnight(y, m, d, timeZone), to: zonedMidnight(y, m, d + 1, timeZone) };
    case 'week': {
      const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Monday = 0
      return { from: zonedMidnight(y, m, d - dow, timeZone), to: end };
    }
    case 'month':
      return { from: zonedMidnight(y, m, 1, timeZone), to: end };
    case 'last_month':
      return { from: zonedMidnight(y, m - 1, 1, timeZone), to: zonedMidnight(y, m, 1, timeZone) };
  }
}

// ─────────────── Feedback (Phase 8) ───────────────

export const feedbackAnswerSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('MET'), rating: z.number().int().min(1).max(5), comment: z.string().trim().max(300).optional() }),
  z.object({ outcome: z.literal('NOT_MET_YET') }),
  z.object({ outcome: z.literal('CANCELED'), reason: z.enum(['PRICE', 'CHANGED_MIND', 'PROVIDER_CANCELED', 'OTHER']) }),
]);

/** Short codes used in bot buttons (Telegram limits callback data to 64 bytes). */
export const FEEDBACK_CALLBACK = { met: 'fbm', notYet: 'fbn', canceled: 'fbc', rate: 'fbr', reason: 'fbx' } as const;

// ─────────────── Onboarding ───────────────

export const onboardingItemSchema = z.object({
  role: z.enum(ROLES),
  kind: z.enum(['CHECKLIST', 'TUTORIAL']),
  titleEn: z.string().trim().min(1).max(80),
  titleAm: z.string().trim().min(1).max(80),
  bodyEn: z.string().trim().max(500).nullable().optional(),
  bodyAm: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.number().int().default(0),
  enabled: z.boolean().default(true),
});

// ─────────────── Admin (Phase 9) ───────────────

export const BROADCAST_AUDIENCES = ['CLIENTS', 'PROVIDERS', 'AGENTS', 'ALL'] as const;
export const BROADCAST_MEDIA_TYPES = ['PHOTO', 'VIDEO', 'VOICE'] as const;
/** Text alone up to 3500 characters; with a photo, video or voice it becomes the caption (Telegram allows 1024). */
export const broadcastSchema = z
  .object({
    audience: z.enum(BROADCAST_AUDIENCES),
    text: z.string().trim().max(3500).default(''),
    mediaType: z.enum(BROADCAST_MEDIA_TYPES).optional(),
    mediaKey: z.string().max(200).optional(),
  })
  .refine((b) => !!b.text || !!b.mediaKey, { message: 'EMPTY_BROADCAST' })
  .refine((b) => !b.mediaKey || b.text.length <= 1024, { message: 'CAPTION_TOO_LONG', path: ['text'] })
  .refine((b) => !b.mediaKey === !b.mediaType, { message: 'MEDIA_TYPE_REQUIRED', path: ['mediaType'] });
export type BroadcastInput = z.infer<typeof broadcastSchema>;

export const supportReplySchema = z.object({ text: z.string().trim().min(1).max(3000) });

export const contentBlockSchema = z.object({
  bodyEn: z.string().max(4000),
  bodyAm: z.string().max(4000),
});

/** Content an admin can edit. Each has a built-in fallback in the app's text. */
export const CONTENT_KEYS = ['welcome', 'warning', 'how_it_works', 'faq', 'payment_instructions', 'announcement'] as const;
export type ContentKey = (typeof CONTENT_KEYS)[number];

export const userActionSchema = z.object({ reason: z.string().trim().max(300).optional() });
export const reassignAgentSchema = z.object({ agentId: z.string().uuid().nullable() });
